/** @jsxImportSource @emotion/react */
import React from 'react';
import { Button, Card, Space, Tag, Typography } from 'antd';
import type { CropAttributeSummary } from '../../models/Crop';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import commonStyles from '../../styles/commonStyles';

const { Text } = Typography;

const styles = {
  headerContainer: css({ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }),
  zeroMarginTag: css({ margin: 0 }),
};

interface AttributeCardProps {
  attribute: CropAttributeSummary;
  onEdit: (attribute: CropAttributeSummary) => void;
}

export const AttributeCard: React.FC<AttributeCardProps> = ({ attribute, onEdit }) => {
  const { t } = useTranslation('crops');
  const options = [...(attribute.options || [])].sort((a, b) => {
    const left = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const right = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (left === right) return String(a.title).localeCompare(String(b.title));
    return left - right;
  });

  return (
    <Card key={String(attribute.id)} type="inner" size="small">
      <Space direction="vertical" size={4} css={commonStyles.fullWidth}>
        <div css={styles.headerContainer}>
          <Space align="center" wrap>
            <Text strong>{attribute.name}</Text>
            {attribute.description && <Text type="secondary">— {attribute.description}</Text>}
          </Space>
          <Button size="small" type="link" onClick={() => onEdit(attribute)}>
            {t('common:actions.edit')}
          </Button>
        </div>
        {options.length > 0 ? (
          <Space wrap size={4}>
            {options.map((option) => (
              <Tag key={`${attribute.id}-${option.id ?? option.code}`} css={styles.zeroMarginTag}>
                {option.title}
                {option.code && option.code !== option.title ? ` (${option.code})` : ''}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary" css={commonStyles.smallText}>{t('attributes.freeValue')}</Text>
        )}
      </Space>
    </Card>
  );
};